import React, { useState, useEffect, useMemo } from "react";
import { ToastContainer, toast } from 'react-toastify';
import EditSubscriptionModal from './EditSubscriptionModal';
import { Subscription } from './types';
import './Admin.css';
import { useRole } from '../../hooks/useRole';

// Define all possible columns based on Subscription type and potential display preferences
const ALL_COLUMNS: Array<keyof Subscription | string> = [
    "status", "userId", "planId", "customerId",
    "currentCredits", "createdAt", "nextBillingDate",
    "period", "subscriptionId", "cancelledAt", "cancelledReason",
    "nextPlanId", "lastCreditsUpdate", "trialEndAt"
];

// Define the default visible columns
const DEFAULT_VISIBLE_COLUMNS: Array<keyof Subscription | string> = [
    "status", "userId", "planId", "currentCredits", "createdAt", "nextBillingDate", "period"
];

// Type for column visibility state
type ColumnVisibility = { [key in keyof Subscription | string]?: boolean };

const Subscriptions = () => {
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [sortField, setSortField] = useState<keyof Subscription | string>("createdAt");
    const [sortDirection, setSortDirection] = useState("desc");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const { hasRole } = useRole();
    const [lastClickTime, setLastClickTime] = useState(0);


    // State for column visibility - load from localStorage or default to all true
    const [visibleColumns, setVisibleColumns] = useState<ColumnVisibility>(() => {
        const savedColumns = localStorage.getItem('subscriptionTableVisibleColumns');
        let initialVisibility: ColumnVisibility = {};
        try {
            if (savedColumns) {
                initialVisibility = JSON.parse(savedColumns);
                // Ensure all columns from ALL_COLUMNS are present, default new ones to true (consistent with previous behavior)
                ALL_COLUMNS.forEach(col => {
                    if (initialVisibility[col] === undefined) {
                        // Keep defaulting *new* columns to true when loading from storage
                        initialVisibility[col] = true; 
                    }
                });
            } else {
                // Set default visibility based on DEFAULT_VISIBLE_COLUMNS
                ALL_COLUMNS.forEach(col => {
                    initialVisibility[col] = DEFAULT_VISIBLE_COLUMNS.includes(col);
                });
            }
        } catch (error) {
            console.error("Error parsing visible columns from localStorage:", error);
            // Fallback to default visibility in case of parsing error
            ALL_COLUMNS.forEach(col => {
                initialVisibility[col] = DEFAULT_VISIBLE_COLUMNS.includes(col);
            });
        }
        return initialVisibility;
    });

    // Effect to save visibleColumns to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('subscriptionTableVisibleColumns', JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/get-subscriptions`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
        }).then(response => response.json()).then(data => {
            if (data.subscriptions) {
                // Сортируем подписки по createdAt в порядке убывания при загрузке
                const sortedData = [...data.subscriptions].sort((a, b) => {
                    if (!a.createdAt) return 1;
                    if (!b.createdAt) return -1;
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                });
                setSubscriptions(sortedData);
            }
        });
    }, []);

    const handleSort = (field: keyof Subscription | string) => {
        const newDirection = field === sortField && sortDirection === "asc" ? "desc" : "asc";
        setSortField(field);
        setSortDirection(newDirection);
        const sortedData = [...subscriptions].sort((a, b) => {
            if (a[field as keyof Subscription] === null) return 1;
            if (b[field as keyof Subscription] === null) return -1;

            // Ensure comparison works for different types potentially
            const valA = a[field as keyof Subscription];
            const valB = b[field as keyof Subscription];

            const compareResult = valA < valB ? -1 : valA > valB ? 1 : 0;
            return newDirection === "asc" ? compareResult : -compareResult;
        });

        setSubscriptions(sortedData);
    };

    const handleEdit = (subscription: Subscription) => {
        setEditingSubscription(subscription);
    };

    const handleSave = async (updatedSubscription: Subscription) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/update-subscription`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify(updatedSubscription),
            });
            if (response.ok) {
                toast.success("Subscription updated successfully", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            } else {
                toast.error("Failed to update subscription", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            }
            setEditingSubscription(null);

            fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/get-subscriptions`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`,
                },
            }).then(response => response.json()).then(data => {
                if (data.subscriptions) {
                    setSubscriptions(data.subscriptions);
                }
            });
        } catch (error) {
            toast.error("Failed to update subscription", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const formatId = (id: string) => {
        return `...${id.slice(-4)}`;
    };

    // Memoize visible columns array to avoid recalculating on every render
    const currentlyVisibleColumns = useMemo(() => {
        return ALL_COLUMNS.filter(col => visibleColumns[col]);
    }, [visibleColumns]);

    // Toggle column visibility
    const toggleColumnVisibility = (column: keyof Subscription | string) => {
        setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }));
    };

    const filteredSubscriptions = subscriptions.filter(sub => {
        const searchStr = searchQuery.toLowerCase();
        // Search only in visible columns
        return currentlyVisibleColumns.some(col => {
            const value = sub[col as keyof Subscription];
            // Handle potential null/undefined values and convert numbers to string
            return value !== null && value !== undefined && String(value).toLowerCase().includes(searchStr);
        });
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredSubscriptions.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => {
        setTotalPages(Math.ceil(filteredSubscriptions.length / itemsPerPage));
        setCurrentPage(1);
    }, [filteredSubscriptions.length, itemsPerPage]);

    const Pagination = () => {
        const pageNumbers = [];
        for (let i = 1; i <= totalPages; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="pagination">
                <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                    disabled={currentPage === 1}
                    className="pagination-button"
                >
                    ←
                </button>
                {pageNumbers.map(number => (
                    <button
                        key={number}
                        onClick={() => setCurrentPage(number)}
                        className={`pagination-button ${currentPage === number ? 'active' : ''}`}
                    >
                        {number}
                    </button>
                ))}
                <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                >
                    →
                </button>
                <select 
                    value={itemsPerPage} 
                    onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                    }}
                    className="items-per-page"
                >
                    <option value="10">10 on page</option>
                    <option value="20">20 on page</option>
                    <option value="50">50 on page</option>
                </select>
            </div>
        );
    };

    // Helper function to get cell value, handling potential nested objects like cancelledReason
    const getCellValue = (sub: Subscription, column: keyof Subscription | string): React.ReactNode => {
        if (column === 'cancelledReason') {
            const reason = sub.cancelledReason;
            return reason ? `${reason.reason || ''} ${reason.comment || ''} ${reason.feedback || ''}`.trim() || '-' : '-';
        }
        if (column === 'createdAt' || column === 'nextBillingDate' || column === 'cancelledAt' || column === 'lastCreditsUpdate' || column === 'trialEndAt') {
            return formatDate(sub[column as keyof Subscription] as string);
        }
        if (column === '_id') {
             return formatId(sub._id);
        }
        // Handle other potential null/undefined values
        const value = sub[column as keyof Subscription];
        return value !== null && value !== undefined ? String(value) : '-';
    };

    const handleCopyUserId = (userId: string) => {
        navigator.clipboard.writeText(userId).then(() => {
            toast.success("UserId copied", { closeButton: false, hideProgressBar: true, autoClose: 2000 });
        }).catch(() => {
            toast.error("Failed to copy UserId", { closeButton: false, hideProgressBar: true, autoClose: 2000 });
        });
    };

    const handleRowClick = (userId: string) => {
        const now = Date.now();
        if (now - lastClickTime < 500) { // Увеличим интервал до 500мс для надёжности
            return;
        }
        setLastClickTime(now);
        handleCopyUserId(userId);
    };

    const handleRowDoubleClick = (subscription: Subscription) => {
        if (hasRole('admin')) {
            handleEdit(subscription);
        }
    };

    return (
        <div className="admin-container">
            <h2>Subscriptions</h2>
            <div className="search-container">
                <input
                    type="text"
                    placeholder="Search in subscriptions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                />
            </div>
            {/* Column Selector Row */}
            <div className="column-selector-row">
                {ALL_COLUMNS.map(col => (
                    <div key={col} className="column-selector-item">
                        <input 
                            type="checkbox"
                            id={`col-${col}`}
                            checked={!!visibleColumns[col]}
                            onChange={() => toggleColumnVisibility(col)}
                        />
                        <label htmlFor={`col-${col}`}>{col}</label>
                    </div>
                ))}
            </div>
            <div className="subscriptions-table-container">
                <table className="subscriptions-table">
                    <thead>
                        <tr>
                            {/* Render headers based on currentlyVisibleColumns */}
                            {currentlyVisibleColumns.map((field) => (
                                <th key={field} onClick={() => handleSort(field)}>
                                    {field}
                                    {sortField === field && (
                                        <span>{sortDirection === "asc" ? " ↑" : " ↓"}</span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((sub) => (
                            <tr 
                                key={sub._id} 
                                onClick={() => handleRowClick(sub.userId)}
                                onDoubleClick={hasRole('admin') ? () => handleRowDoubleClick(sub) : undefined}
                                className={`${hasRole('admin') ? "editable-row" : ""} clickable-row`}
                            >
                                {/* Render cells based on currentlyVisibleColumns */}
                                {currentlyVisibleColumns.map(col => (
                                     <td key={col} title={String(sub[col as keyof Subscription] ?? '')}> 
                                        {getCellValue(sub, col)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Pagination />
            </div>
            {editingSubscription && (
                <EditSubscriptionModal
                    subscription={editingSubscription}
                    onSave={handleSave}
                    onClose={() => setEditingSubscription(null)}
                />
            )}
            <ToastContainer />
        </div>
    );
};

export default Subscriptions; 